<template>
  <modal title="Build a tracking link" :show-footer="false" @close="$emit('close')">
    <div class="utm-layout">
      <div class="utm-questions">
        <div class="form-group">
          <label for="utm-source">Who is sharing this link?</label>
          <input id="utm-source" ref="sourceInput" v-model.trim="source" type="text" class="form-control"
            placeholder="A person's name, a company, a partner ...">
        </div>

        <div class="form-group">
          <label for="utm-medium">Where will it be shared?</label>
          <select id="utm-medium" v-model="selectedMediumValue" class="form-control">
            <option value="">Pick where it will be shared</option>
            <optgroup v-for="group in mediumGroups" :key="group.group" :label="group.group">
              <option v-for="opt in group.options" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </optgroup>
          </select>

          <div v-if="mediumHint" class="utm-hint">
            <i class="fa fa-info-circle"></i> {{ mediumHint }}
          </div>

          <div v-if="isOtherMedium" class="utm-other">
            <input id="utm-medium-other" v-model.trim="otherMedium" type="text" class="form-control"
              placeholder="Describe it in one word, eg webinar">
          </div>
        </div>

        <div class="form-group">
          <label for="utm-content">Anywhere more specific? (optional)</label>
          <input id="utm-content" v-model.trim="content" type="text" class="form-control"
            :placeholder="contentPlaceholder">
        </div>
      </div>

      <div class="utm-result">
        <div class="utm-result__header">Your tracking link</div>

        <div class="utm-result__body">
          <div class="utm-result__url-row">
            <div class="utm-preview">
              <span>{{ baseUrl }}?utm_campaign={{ encodedCampaign }}</span>
              <template v-for="part in dynamicParts">
                <!-- Filled parts show their real value; empty required parts keep
                     a faint placeholder; empty optional parts are hidden. -->
                <template v-if="part.value">
                  <span :key="part.key">&amp;{{ part.param }}=</span><span :key="part.key + '-val'"
                    class="utm-value">{{ encodeParam(part.value) }}</span>
                </template>
                <template v-else-if="!part.optional">
                  <span :key="part.key">&amp;{{ part.param }}=</span><span :key="part.key + '-ph'"
                    class="utm-placeholder">{{ part.placeholderLabel }}</span>
                </template>
              </template>
            </div>
            <button type="button" class="btn btn-primary utm-copy-btn" :disabled="!isComplete" @click="copyLink">
              <i class="fa" :class="copied ? 'fa-check' : 'fa-clipboard'"></i>
              {{ copied ? 'Copied' : 'Copy' }}
            </button>
          </div>

          <hr class="utm-result__divider">

          <p v-if="!isComplete" class="utm-incomplete">
            <i class="fa fa-info-circle"></i>
            Fill in <strong>who is sharing</strong> and <strong>where</strong> to finish
            your link and create its QR code.
          </p>

          <div v-else class="utm-result__qr">
            <!-- Level 'L' (~7% redundancy) keeps the module count down for these
                 long URLs, so the code is less dense and easier to scan. -->
            <qrcode ref="qrcode" class="utm-qrcode-img" :value="finalUrl"
              :options="{ width: 150, margin: 1, errorCorrectionLevel: 'L' }" />
            <div class="utm-result__qr-text">
              <div class="utm-result__qr-title">QR code</div>
              <p class="utm-result__qr-hint">Copy or download to share this link.</p>
              <div class="utm-qr-actions">
                <button type="button" class="btn btn-default btn-sm" @click="copyQr">
                  <i class="fa" :class="qrCopied ? 'fa-check' : 'fa-clipboard'"></i>
                  {{ qrCopied ? 'Copied' : 'Copy QR' }}
                </button>
                <button type="button" class="btn btn-default btn-sm" @click="downloadQr">
                  <i class="fa fa-download"></i> Download QR
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import moment from 'moment';
import Modal from '@/components/utils/Modal';

const DEFAULT_DOMAIN = 'readaloudchallenge.co.uk';
const OTHER_VALUE = '__other__';

export default {
  name: 'UtmLinkBuilder',
  components: {
    modal: Modal,
  },
  props: {
    challengeSlug: { type: String, required: true },
    domain: { type: String, default: null },
  },
  data() {
    return {
      source: '',
      content: '',
      selectedMediumValue: '',
      otherMedium: '',
      copied: false,
      copiedTimer: null,
      qrCopied: false,
      qrCopiedTimer: null,
      mediumGroups: [
        {
          group: 'Social media',
          options: [
            { label: 'Facebook', value: 'facebook' },
            { label: 'LinkedIn', value: 'linkedin' },
            { label: 'X / Twitter', value: 'twitter' },
            { label: 'Instagram', value: 'instagram' },
            { label: 'Social media (other / unspecified)', value: 'social' },
          ],
        },
        {
          group: 'Direct channels',
          options: [
            { label: 'Email', value: 'email' },
            { label: 'Newsletter', value: 'newsletter' },
            { label: 'WhatsApp', value: 'whatsapp' },
            { label: 'Website', value: 'web' },
          ],
        },
        {
          group: 'Offline & events',
          options: [
            { label: 'Printed material', value: 'print' },
            { label: 'Event', value: 'event' },
            { label: 'QR code', value: 'qr' },
          ],
        },
        {
          group: 'Something else',
          options: [
            { label: 'Other (I will describe it)', value: OTHER_VALUE },
          ],
        },
      ],
      mediumHints: {
        social: 'This is fine, but if you know the exact platform (like Facebook or LinkedIn), choosing it gives you better insight into what is working.',
        qr: 'QR codes work, but they are a catch-all. If you know where the QR code will actually be seen (a poster at an event, a flyer), consider picking that instead, or add a specific detail below.',
        web: "'Website' is quite general. If you can, add a specific detail below (like the page or site name).",
      },
      // Tailor the optional 'more specific' example to the chosen medium, so the
      // hint is always relevant to where they are sharing.
      contentPlaceholders: {
        facebook: 'e.g. the page or group name',
        instagram: 'e.g. the account or hashtag',
        linkedin: 'e.g. the page or group name',
        twitter: 'e.g. the account or hashtag',
        social: 'e.g. the platform or group name',
        email: "e.g. Summer email 3, or 'welcome email'",
        newsletter: 'e.g. June newsletter',
        whatsapp: 'e.g. the group or broadcast name',
        web: 'e.g. the website or page name (not the address)',
        print: 'e.g. poster, flyer, or magazine name',
        event: 'e.g. the event name',
        qr: 'e.g. where the QR code appears',
      },
    };
  },
  computed: {
    resolvedDomain() {
      return (this.domain && this.domain.trim()) || DEFAULT_DOMAIN;
    },
    baseUrl() {
      return `https://${this.resolvedDomain}/`;
    },
    isOtherMedium() {
      return this.selectedMediumValue === OTHER_VALUE;
    },
    mediumToken() {
      if (!this.selectedMediumValue) return '';
      if (this.isOtherMedium) return this.normaliseMediumToken(this.otherMedium);
      return this.selectedMediumValue;
    },
    // Source and medium are required; a link without them is incomplete and we
    // should not let people copy or share it.
    isComplete() {
      return !!this.source && !!this.mediumToken;
    },
    mediumHint() {
      return this.mediumHints[this.selectedMediumValue] || '';
    },
    contentPlaceholder() {
      return this.contentPlaceholders[this.selectedMediumValue]
        || 'e.g. Campaign X - Email Two, or a Facebook group name';
    },
    encodedCampaign() {
      return this.encodeParam(this.challengeSlug);
    },
    // Drives both the preview and the final URL, in fixed order. Optional parts
    // are hidden from the preview when empty; the rest keep a faint placeholder.
    dynamicParts() {
      return [
        {
          key: 'source', param: 'utm_source', value: this.source, placeholderLabel: 'who referred',
        },
        {
          key: 'medium', param: 'utm_medium', value: this.mediumToken, placeholderLabel: "where it's shared",
        },
        {
          key: 'content', param: 'utm_content', value: this.content, optional: true,
        },
      ];
    },
    finalUrl() {
      const params = [`utm_campaign=${this.encodedCampaign}`];
      this.dynamicParts.forEach((part) => {
        if (part.value) {
          params.push(`${part.param}=${this.encodeParam(part.value)}`);
        }
      });
      return `${this.baseUrl}?${params.join('&')}`;
    },
  },
  methods: {
    // Encode a query value, preferring + over %20 for spaces so the link reads
    // more naturally (both are valid in a query string).
    encodeParam(value) {
      return encodeURIComponent(value).replace(/%20/g, '+');
    },
    // Lowercase, internal whitespace to single hyphens (e.g. 'My Podcast' -> 'my-podcast').
    normaliseMediumToken(value) {
      return value.trim().toLowerCase().replace(/\s+/g, '-');
    },
    // Lowercase, non-alphanumerics to hyphens, no leading/trailing hyphens.
    slugify(value) {
      return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    qrFileName() {
      return [this.challengeSlug, this.source, 'qr', moment().format('YYYYMMDD-HHmmss')]
        .map((part) => this.slugify(part))
        .filter(Boolean)
        .join('-')
        .concat('.png');
    },
    // vue-qrcode renders the <canvas> as its root element, so its $el is the canvas.
    qrCanvas() {
      return this.$refs.qrcode.$el;
    },
    downloadQr() {
      const link = document.createElement('a');
      link.href = this.qrCanvas().toDataURL('image/png');
      link.download = this.qrFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    copyQr() {
      // Image clipboard write is not universal even on modern browsers, so fall
      // back to a download where it is unavailable.
      if (!window.ClipboardItem) {
        this.downloadQr();
        return;
      }
      this.qrCanvas().toBlob((blob) => {
        const item = new window.ClipboardItem({ 'image/png': blob });
        navigator.clipboard.write([item])
          .then(() => this.flagQrCopied())
          .catch(() => this.downloadQr());
      }, 'image/png');
    },
    copyLink() {
      if (!this.isComplete) return;
      navigator.clipboard.writeText(this.finalUrl).then(() => this.flagCopied());
    },
    flagCopied() {
      this.copied = true;
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    flagQrCopied() {
      this.qrCopied = true;
      if (this.qrCopiedTimer) clearTimeout(this.qrCopiedTimer);
      this.qrCopiedTimer = setTimeout(() => {
        this.qrCopied = false;
      }, 2000);
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.sourceInput) this.$refs.sourceInput.focus();
    });
  },
  beforeDestroy() {
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    if (this.qrCopiedTimer) clearTimeout(this.qrCopiedTimer);
  },
};
</script>

<style lang="scss" scoped>
// The shared Modal has no width hook, so reach in via ::v-deep to widen it.
::v-deep .modal-dialog {
  @media (min-width: 768px) {
    width: 1040px;
    max-width: 95%;
  }
}

// Custom flex columns rather than Bootstrap's grid, which carries a stray
// global `.col-md-5 input { height: 2em }` rule that shrank the inputs.
.utm-layout {
  @media (min-width: 768px) {
    display: flex;
    align-items: flex-start;
    column-gap: 24px;
  }
}

.utm-questions {
  @media (min-width: 768px) {
    flex: 0 0 42%;
    max-width: 42%;
  }

  // Match the text inputs to the app's 38px select.form-control height.
  .form-control {
    height: 38px;
  }
}

.utm-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f4f8fb;
  border-left: 3px solid #5b9bd5;
  border-radius: 3px;
  font-size: 0.9em;
  color: #4a6275;
}

.utm-other {
  margin-top: 12px;
}

.utm-result {
  border: 1px solid #d2d6de;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;

  @media (min-width: 768px) {
    flex: 1 1 auto;
    min-width: 0;
  }

  @media (max-width: 767px) {
    margin-top: 20px;
  }

  &__header {
    padding: 8px 14px;
    background: #f9f9f9;
    border-bottom: 1px solid #d2d6de;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6c757d;
  }

  &__body {
    padding: 14px;
  }

  // URL field and copy button share one rounded outline.
  &__url-row {
    display: flex;
    align-items: stretch;
    border: 1px solid #d2d6de;
    border-radius: 4px;
    overflow: hidden;
  }

  &__divider {
    margin: 14px 0;
    border: 0;
    border-top: 1px solid #ededed;
  }

  &__qr {
    display: flex;
    align-items: center;
    column-gap: 16px;
  }

  &__qr-text {
    min-width: 0;
  }

  &__qr-title {
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__qr-hint {
    margin: 0 0 10px;
    font-size: 0.85em;
    color: #6c757d;
  }
}

.utm-preview {
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 12px;
  background: #f9f9f9;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 0.85em;
  line-height: 1.7;
  word-break: break-all;
  color: #337ab7;
}

// The answer values, tinted and bold so they stand out from the utm_* keys.
.utm-value {
  color: #c0563b;
  font-weight: 600;
}

.utm-placeholder {
  color: #a0a0a0;
  border-bottom: 1px dashed #c0c0c0;
}

.utm-copy-btn {
  flex: 0 0 auto;
  border: 0;
  border-left: 1px solid #d2d6de;
  border-radius: 0;
  align-self: stretch;

  .fa {
    margin-right: 4px;
  }
}

.utm-incomplete {
  margin: 0;
  font-size: 0.9em;
  color: #6c757d;

  .fa {
    margin-right: 4px;
  }
}

.utm-qrcode-img {
  flex: 0 0 auto;
  line-height: 0;
  padding: 6px;
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 4px;
}

.utm-qr-actions {
  display: flex;
  column-gap: 8px;
}
</style>
